import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

import LcvOrderImportHistoryGrid from "./LcvOrderImportHistoryGrid";
import LcvOrderPreviewSkeleton from "./LcvOrderPreviewSkeleton";
import { lcvOrdersConstants } from "../../../../../_redux/_constants/lcvOrders.constants";
import {
     lcvOrdersImportRequest,
     lcvOrdersPreviewImportRequest,
} from "../../../../../_redux/_actions/lcvOrders.action";

// The file is sent to the backend to be read, so the rules below and the parsing are one and the
// same. Nothing is stored until Import is pressed.

// Shown before a file is picked, so an administrator knows what pressing import will do before it
// happens rather than after. These must stay in step with the rules the backend applies.
const UPLOAD_RULES = [
     "Orders are keyed on the customer order number, so uploading the same file twice is safe.",
     "A row that fails validation is reported against itself and never stops the rest importing.",
     "A dealer code we do not know is created and flagged as LCV.",
     "An order counts towards a game code only when it is registered, carries a qualifying sales channel, and both its order date and registration date sit inside the campaign window.",
     "Game codes are issued as part of the import, three qualifying orders to a code. Each dealership is recounted, so a repeated file issues nothing further and no code is ever taken back.",
];

const VALID_TAB = "valid";
const INVALID_TAB = "invalid";

// The sales channels the campaign runs on. The API drops everything else before it answers, so
// this is a guard rather than a filter: if a row on another channel ever reaches the screen it is
// a fault upstream, and showing it would imply the order is about to be imported when it is not.
const CAMPAIGN_SALES_CHANNELS = ["5B", "5C", "5D", "5E", "5F", "5J", "5L", "5S"];

function isCampaignRow(row) {
     return CAMPAIGN_SALES_CHANNELS.includes((row.salesChannel || "").toUpperCase());
}

// The preview arrives whole, so the page is taken here. Ten rows a page is what every other table
// in the portal shows.
const INITIAL_PAGE = { skip: 0, take: 10 };

const dateCell = (field) => (cellProps) => (
     <td>
          {cellProps.dataItem[field]
               ? new Date(cellProps.dataItem[field]).toLocaleDateString()
               : ""}
     </td>
);

const yesNoCell = (field) => (cellProps) => <td>{cellProps.dataItem[field] ? "Yes" : "No"}</td>;

// The row carries its own message, so the reason a row is refused comes from the backend rather
// than from a second set of rules written here.
const problemCell = (cellProps) => (
     <td>{(cellProps.dataItem.errors || []).join(" ") || cellProps.dataItem.message}</td>
);

export default function LcvOrderImport() {
     const actionDispatch = useDispatch();

     const preview = useSelector((state) => state?.lcvOrders?.preview);

     const pendingRequests = useSelector((state) => state?.loading?.pendingRequests || []);

     const isPreviewing = pendingRequests.includes(
          lcvOrdersConstants.LCV_ORDERS_PREVIEW_IMPORT_REQUEST,
     );

     const isImporting = pendingRequests.includes(lcvOrdersConstants.LCV_ORDERS_IMPORT_REQUEST);

     const [file, setFile] = useState(null);
     const [tab, setTab] = useState(VALID_TAB);
     const [page, setPage] = useState(INITIAL_PAGE);

     const readFile = (event) => {
          const chosen = event.target.files && event.target.files[0];

          if (!chosen) {
               return;
          }

          setFile(chosen);
          setTab(VALID_TAB);
          setPage(INITIAL_PAGE);

          actionDispatch(lcvOrdersPreviewImportRequest({ file: chosen }));
     };

     const rows = useMemo(() => (preview?.rows || []).filter(isCampaignRow), [preview]);

     const validRows = useMemo(() => rows.filter((row) => row.isValid), [rows]);
     const invalidRows = useMemo(() => rows.filter((row) => !row.isValid), [rows]);

     const shownRows = tab === VALID_TAB ? validRows : invalidRows;

     const pageRows = shownRows.slice(page.skip, page.skip + page.take);

     const submit = () => {
          actionDispatch(lcvOrdersImportRequest({ file }));
     };

     return (
          <div>
               <Typography variant="body1" sx={{ mb: 2 }}>
                    Upload the Renault Commandes spreadsheet, check what it would do, then import it.
               </Typography>

               {!file && (
                    <Box component="section" sx={{ mb: 3 }}>
                         <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                              What this upload does
                         </Typography>

                         <Box component="ul" sx={{ m: 0, pl: 3 }}>
                              {UPLOAD_RULES.map((rule) => (
                                   <Typography component="li" variant="body2" key={rule} sx={{ mb: 0.5 }}>
                                        {rule}
                                   </Typography>
                              ))}
                         </Box>
                    </Box>
               )}

               <Box sx={{ mb: 2 }}>
                    <input type="file" accept=".xlsx" onChange={readFile} disabled={isPreviewing} />
               </Box>

               {file && <Typography variant="body2" sx={{ mb: 1 }}>{file.name}</Typography>}

               {isPreviewing && <LcvOrderPreviewSkeleton />}

               {/* A file that came back with nothing parsed leaves a message on screen rather than
                   an empty page. The reason itself is on the error banner, from the backend. */}
               {file && !isPreviewing && !preview && (
                    <Typography variant="body2" sx={{ mb: 1, color: "error.main" }}>
                         Nothing was read from that file. Check the message above, then try again.
                    </Typography>
               )}

               {!isPreviewing && preview && (
                    <>
                         {/* The headline is the backend's own message. Only the counts it does not
                             already carry are added underneath. */}
                         <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {preview.message}
                         </Typography>

                         <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {preview.valid} valid, {preview.invalid} invalid,{" "}
                              {preview.unknownDealers} at a dealer we do not hold.
                         </Typography>

                         {/* Which campaign each order goes to is decided by its order date, so the
                             split is shown before anything is saved. */}
                         <Alert severity="info" sx={{ mb: 2 }}>
                              Campaigns: {preview.campaignSummary}
                         </Alert>

                         <Tabs
                              value={tab}
                              onChange={(event, chosen) => {
                                   setTab(chosen);
                                   setPage(INITIAL_PAGE);
                              }}
                              sx={{ mb: 1 }}
                         >
                              <Tab value={VALID_TAB} label={`Valid (${validRows.length})`} />
                              <Tab value={INVALID_TAB} label={`Invalid (${invalidRows.length})`} />
                         </Tabs>

                         <Grid
                              data={pageRows}
                              className="sba-area-data-grid"
                              scrollable="none"
                              skip={page.skip}
                              take={page.take}
                              total={shownRows.length}
                              pageable={{ buttonCount: 4, pageSizes: [10, 25, 50] }}
                              onPageChange={(event) => setPage(event.page)}
                         >
                              <GridColumn field="rowNumber" title="Row" />
                              <GridColumn field="customerOrderNumber" title="Order" />
                              <GridColumn field="chassisNumber" title="Chassis" />
                              <GridColumn field="registrationNumber" title="Registration" />
                              <GridColumn field="dealerCode" title="Dealer Code" />
                              <GridColumn field="dealerName" title="Dealer" />
                              <GridColumn field="salesChannel" title="Channel" />
                              <GridColumn field="campaignName" title="Campaign" />
                              <GridColumn
                                   field="customerOrderCreationDate"
                                   title="Ordered"
                                   cells={{ data: dateCell("customerOrderCreationDate") }}
                              />
                              <GridColumn
                                   field="registrationDate"
                                   title="Registered"
                                   cells={{ data: dateCell("registrationDate") }}
                              />
                              <GridColumn
                                   field="isKnownDealer"
                                   title="Known Dealer"
                                   cells={{ data: yesNoCell("isKnownDealer") }}
                              />
                              <GridColumn
                                   field="isEligible"
                                   title="Earns A Code"
                                   cells={{ data: yesNoCell("isEligible") }}
                              />
                              {tab === VALID_TAB ? (
                                   <GridColumn
                                        field="eligibilityReason"
                                        title="Why"
                                   />
                              ) : (
                                   <GridColumn title="What is wrong" cells={{ data: problemCell }} />
                              )}
                         </Grid>

                         <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
                              <Button
                                   type="button"
                                   themeColor="primary"
                                   disabled={!file || isImporting || isPreviewing}
                                   onClick={submit}
                              >
                                   {isImporting
                                        ? "Importing..."
                                        : `Import ${preview.total} row${preview.total === 1 ? "" : "s"}`}
                              </Button>

                              {invalidRows.length > 0 && (
                                   <Typography variant="body2">
                                        The {invalidRows.length} invalid row
                                        {invalidRows.length === 1 ? "" : "s"} will be reported and
                                        skipped. Everything else imports.
                                   </Typography>
                              )}
                         </Box>
                    </>
               )}

               {/* Only ever one table on screen. The history belongs to the idle state, so it gives
                   way to the rows being checked and to the outcome afterwards. */}
               {!file && <LcvOrderImportHistoryGrid />}
          </div>
     );
}
